import { useEffect, useMemo, memo } from 'react';
import {
    Box,
    Flex,
    Grid,
    GridItem,
    Heading,
    Text,
    Button,
    Divider,
    Image,
    Skeleton,
    SkeletonText,
    useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { DashboardSection } from './components/DashboardStyles';
import BottomBg from 'assets/CartSummaryBg.png';
import { addCommasToNumber, calculateCartTotal } from 'utils/utils';
import CartBox from 'components/Cart/CartBox';
import { view_cart } from 'redux/actions/productActions';
import { useCurrency } from 'hooks/useCurrency';
import CheckoutModal from './components/CheckoutModal';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — matches the rest of the app
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT = "'Bricolage Grotesque', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const ArrowLeftIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CartIcon = (props) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 4h2l2.4 12.2a2 2 0 002 1.8h8.7a2 2 0 002-1.6L22 8H6"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="10" cy="21" r="1.5" fill="currentColor" />
        <circle cx="18" cy="21" r="1.5" fill="currentColor" />
    </svg>
);

const InfoIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   ITEM COUNT FORMATTER
   ───────────────────────────────────────────────────────────── */

const formatItemCount = (n) => {
    const count = Number(n) || 0;
    return `${count} ${count === 1 ? 'item' : 'items'}`;
};

/* ─────────────────────────────────────────────────────────────
   CART ITEM SKELETON
   ───────────────────────────────────────────────────────────── */

const CartItemSkeleton = memo(function CartItemSkeleton() {
    return (
        <Box>
            <Flex gap="14px" py="14px">
                <Skeleton w="100px" h="100px" borderRadius="10px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Box flex="1">
                    <SkeletonText noOfLines={2} spacing="2" skeletonHeight="3" startColor={SURFACE_ALT} endColor={BORDER} />
                    <Skeleton mt="3" h="4" w="40%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
                    <Skeleton mt="3" h="6" w="120px" borderRadius="6px" startColor={SURFACE_ALT} endColor={BORDER} />
                </Box>
            </Flex>
            <Divider borderColor={BORDER} />
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   EMPTY CART STATE
   ───────────────────────────────────────────────────────────── */

const EmptyCart = memo(function EmptyCart() {
    return (
        <Flex
            direction="column"
            align="center"
            justify="center"
            py={{ base: '60px', md: '80px' }}
            px="20px"
            textAlign="center"
            gap="14px"
        >
            <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg={CYAN}
                color={NAVY}
                align="center"
                justify="center"
            >
                <CartIcon />
            </Flex>
            <Box>
                <Heading
                    as="h2"
                    fontSize="18px"
                    fontWeight="800"
                    color={INK}
                    fontFamily={FONT}
                    mb="6px"
                >
                    Your cart is empty
                </Heading>
                <Text fontSize="13px" color={MUTED} maxW="320px">
                    Looks like you haven't added anything yet. Start browsing to find products you love.
                </Text>
            </Box>
            <Button
                as={Link}
                to="/dashboard/products"
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                _active={{ bg: NAVY_DEEP }}
                borderRadius="8px"
                h="42px"
                px="20px"
                fontSize="13px"
                fontWeight="700"
                fontFamily={FONT}
                mt="6px"
            >
                Browse products
            </Button>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   SUMMARY ROW (for subtotal, shipping, tax)
   ───────────────────────────────────────────────────────────── */

const SummaryRow = memo(function SummaryRow({ label, value, helper, isTotal }) {
    return (
        <Flex justify="space-between" align="flex-start" gap="10px">
            <Flex direction="column" gap="2px" minW="0">
                <Text
                    fontSize={isTotal ? '14px' : '12.5px'}
                    color={isTotal ? INK : MUTED}
                    fontWeight={isTotal ? '800' : '500'}
                    fontFamily={FONT}
                >
                    {label}
                </Text>
                {helper && (
                    <Text fontSize="10.5px" color={FAINT}>
                        {helper}
                    </Text>
                )}
            </Flex>
            <Text
                fontSize={isTotal ? '16px' : '13px'}
                color={INK}
                fontWeight={isTotal ? '800' : '600'}
                fontFamily={FONT}
                whiteSpace="nowrap"
            >
                {value}
            </Text>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   CART SUMMARY PANEL
   ───────────────────────────────────────────────────────────── */

const CartSummary = memo(function CartSummary({
    symbol, total, itemCount, onCheckout, isDisabled,
}) {
    const itemsLabel = formatItemCount(itemCount);
    const formattedTotal = `${symbol || '₦'} ${addCommasToNumber(total ?? 0)}`;

    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="14px"
            overflow="hidden"
            boxShadow={SHADOW_SM}
            position={{ base: 'static', lg: 'sticky' }}
            top={{ lg: '88px' }} /* clear header + a bit of breathing room */
            alignSelf="start"
        >
            <Box p="18px 18px 0">
                <Heading
                    as="h2"
                    fontSize="14px"
                    fontWeight="800"
                    color={INK}
                    fontFamily={FONT}
                    mb="4px"
                >
                    Order summary
                </Heading>
                <Text fontSize="11px" color={FAINT} mb="16px">
                    {itemsLabel}
                </Text>
            </Box>

            <Box p="0 18px 18px">
                <Flex direction="column" gap="12px">
                    <SummaryRow
                        label="Subtotal"
                        value={formattedTotal}
                    />
                    <SummaryRow
                        label="Shipping & tax"
                        value="—"
                        helper="Calculated at checkout"
                    />
                    <Divider borderColor={BORDER} />
                    <SummaryRow
                        label="Total"
                        value={formattedTotal}
                        isTotal
                    />
                </Flex>

                <Button
                    onClick={onCheckout}
                    isDisabled={isDisabled}
                    bg={NAVY}
                    color="white"
                    _hover={{ bg: NAVY_DEEP }}
                    _active={{ bg: NAVY_DEEP }}
                    _disabled={{
                        bg: BORDER,
                        color: FAINT,
                        cursor: 'not-allowed',
                        _hover: { bg: BORDER },
                    }}
                    w="full"
                    h="46px"
                    mt="18px"
                    borderRadius="10px"
                    fontWeight="700"
                    fontSize="13px"
                    fontFamily={FONT}
                    aria-label={
                        isDisabled
                            ? 'Checkout disabled — cart is empty'
                            : 'Proceed to checkout'
                    }
                >
                    Proceed to checkout
                </Button>

                <Flex align="center" gap="6px" mt="12px">
                    <Box color={FAINT} flexShrink={0}>
                        <InfoIcon />
                    </Box>
                    <Text fontSize="10.5px" color={FAINT} lineHeight="1.4">
                        Shipping and applicable taxes will be calculated based on your delivery address.
                    </Text>
                </Flex>
            </Box>

            {/* Decorative background — only on desktop where there's room */}
            <Box
                display={{ base: 'none', lg: 'block' }}
                position="relative"
                h="120px"
                overflow="hidden"
                mt="auto"
            >
                <Image
                    src={BottomBg}
                    alt=""
                    role="presentation"
                    position="absolute"
                    bottom="0"
                    left="0"
                    right="0"
                    w="full"
                    objectFit="cover"
                    objectPosition="bottom"
                    opacity={0.85}
                    pointerEvents="none"
                />
            </Box>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

export default function Cart() {
    const dispatch = useDispatch();
    const productReducer = useSelector((state) => state.productReducer);
    const { cartItems, adding, loading } = productReducer;

    const convertedCartTotal = useCurrency(calculateCartTotal(cartItems));

    const { isOpen, onOpen, onClose } = useDisclosure();

    /* Fetch cart on mount + after any add operation */
    useEffect(() => {
        dispatch(view_cart());
    }, [adding, dispatch]);

    const items = useMemo(() => cartItems?.items ?? [], [cartItems]);
    const itemCount = items.length;
    const hasItems = itemCount > 0;

    /* Show skeleton on initial load (no items yet AND loading) */
    const showSkeleton = loading && !cartItems;

    return (
        <DashboardSection>
            <Box maxW="1280px" mx="auto" w="full" px={{ base: '16px', md: '0' }}>
                {/* ── HEADER ── */}
                <Flex
                    align={{ base: 'flex-start', md: 'center' }}
                    justify="space-between"
                    direction={{ base: 'column', md: 'row' }}
                    gap="10px"
                    mb={{ base: '20px', md: '28px' }}
                >
                    <Box>
                        <Heading
                            as="h1"
                            fontSize={{ base: '22px', md: '26px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.1"
                            mb="4px"
                        >
                            Your cart
                        </Heading>
                        <Text fontSize="12px" color={MUTED}>
                            {hasItems ? formatItemCount(itemCount) : 'No items yet'}
                        </Text>
                    </Box>

                    <Flex
                        as={Link}
                        to="/dashboard/products"
                        align="center"
                        gap="6px"
                        color={NAVY}
                        fontSize="12px"
                        fontWeight="700"
                        fontFamily={FONT}
                        _hover={{ textDecoration: 'underline' }}
                    >
                        <ArrowLeftIcon />
                        <Text as="span">Continue shopping</Text>
                    </Flex>
                </Flex>

                {/* ── BODY ── */}
                {showSkeleton ? (
                    <Grid
                        templateColumns={{ base: '1fr', lg: '1fr 360px' }}
                        gap={{ base: '20px', lg: '28px' }}
                    >
                        <GridItem>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <CartItemSkeleton key={i} />
                            ))}
                        </GridItem>
                        <GridItem>
                            <Skeleton h="360px" borderRadius="14px" startColor={SURFACE_ALT} endColor={BORDER} />
                        </GridItem>
                    </Grid>
                ) : !hasItems ? (
                    <Box
                        bg={SURFACE}
                        border="1px solid"
                        borderColor={BORDER}
                        borderRadius="14px"
                        boxShadow={SHADOW_SM}
                    >
                        <EmptyCart />
                    </Box>
                ) : (
                    <Grid
                        templateColumns={{ base: '1fr', lg: '1fr 360px' }}
                        gap={{ base: '20px', lg: '28px' }}
                        alignItems="start"
                    >
                        {/* LEFT — items list */}
                        <GridItem minW="0">
                            <Box
                                bg={SURFACE}
                                border="1px solid"
                                borderColor={BORDER}
                                borderRadius="14px"
                                p={{ base: '14px', md: '18px' }}
                                boxShadow={SHADOW_SM}
                            >
                                <Flex direction="column">
                                    {items.map((item, idx) => (
                                        <Box key={item.item_id}>
                                            <CartBox cartItem={item} />
                                            {idx < items.length - 1 && (
                                                <Divider my="14px" borderColor={BORDER} />
                                            )}
                                        </Box>
                                    ))}
                                </Flex>
                            </Box>
                        </GridItem>

                        {/* RIGHT — summary */}
                        <GridItem minW="0">
                            <CartSummary
                                symbol={convertedCartTotal?.symbol}
                                total={convertedCartTotal?.price}
                                itemCount={itemCount}
                                onCheckout={onOpen}
                                isDisabled={isEmpty(items)}
                            />
                        </GridItem>
                    </Grid>
                )}
            </Box>

            <CheckoutModal isOpen={isOpen} onClose={onClose} />
        </DashboardSection>
    );
}